import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CropAndBrandAvailability7 } from '../../Models/Responses/SeedResponseV7';
import { getSeedsForGrowerV7 } from '../../store/Seeds/SeedsActions';
import { RootState } from '../../store/Store';
import { featureFlagSelector, Features, getFeatureFlag } from '../../store/User/ConfigSlice';
import { BagSizeProvider } from './BagSizeProvider';
import { PricingProvider } from './PricingProvider';
import { ProductRollupCalc } from './ProductRollupCalc';
import { IProductRollupCalcLogger, RelatedObjects } from './ProductRollupCalcLogger';

// Trace logging calculation details is disabled by default.
const enableDebugLogging = false;

/** 
 * Sets up a product rollup calculator for use in UI components. 
 * Make sure loading is false before use.
 */
export const useProductRollup = (seeds: CropAndBrandAvailability7[]) => 
{
	const featureFlags = useSelector(featureFlagSelector);
	const useNewPricingService = getFeatureFlag(featureFlags, Features.UseNewPricingService);

	const logger = useMemo(() => enableDebugLogging ? new ConsoleLogger() : undefined, []);

	const { pricer, bagSizer, calculator } = useMemo(() => 
	{
		const pricer = new PricingProvider(seeds, logger);
		const bagSizer = new BagSizeProvider();
		const calculator = new ProductRollupCalc(pricer, bagSizer, logger);

		return {
			pricer,
			bagSizer,
			calculator,
		};
	}, [seeds, logger]);	

	return {
		calculator,
		pricer,
		bagSizer,
		useNewPricingService
	};
};

/** Generate a new calculator from the redux root state. */
export const calculatorFromStore = (state: RootState) => 
{
	const useNewPricingService = getFeatureFlag(featureFlagSelector(state), Features.UseNewPricingService);

	const seeds = state.seeds.seedsV7.allSeeds;

	if (useNewPricingService && !seeds)
		throw new Error('Attempting to create a calculator without seed data.');

	// Trace logging is disabled by default.
	const logger = enableDebugLogging ? new ConsoleLogger() : undefined;

	const pricer =  new PricingProvider(seeds, logger);
	const bagSizer =  new BagSizeProvider();
	const calculator =  new ProductRollupCalc(pricer, bagSizer, logger);


	return { calculator, useNewPricingService };
};

/** Primarily for debugging and only for use with the product rollup calculator. */
class ConsoleLogger implements IProductRollupCalcLogger
{
	public trace(message: string, relatedObjects?: RelatedObjects): void 
	{
		if (relatedObjects)
			console.log(message, relatedObjects);
		else
			console.log(message);
	}
}